import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { fetchBotStatus, resumeBot, type BotStatus } from "./api";

/** Quanti ms resta visibile il toast di successo. */
const TOAST_MS = 5000;

/** Formatta ``human_until`` (ISO UTC) come orario leggibile per l'operatore.
 *  - Se la scadenza è oggi (ora locale): solo "HH:MM"
 *  - Altrimenti: "gg/mm/aa HH:MM" (formato italiano compatto) */
function formatUntil(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (isToday) {
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Ritorna true se la pausa bot è attiva in questo momento.
 *  Pausa attiva = human_mode true  OPPURE  human_until in futuro. */
function isPauseActive(status: BotStatus): boolean {
  if (status.human_mode) return true;
  if (status.human_until) {
    return new Date(status.human_until) > new Date();
  }
  return false;
}

type Props = {
  threadId: number;
  /** Valore di ``ThreadDetail.source`` (es. "whatsapp", "mirakl"). */
  source: string;
};

/**
 * BotPausePanel — mostra il badge "Bob in pausa" e il bottone
 * "Restituisci a Bob" sui thread WhatsApp con human_mode attivo.
 *
 * Props:
 *  - ``threadId``: id del thread corrente.
 *  - ``source``: sorgente del thread; se !== "whatsapp" il componente
 *    ritorna null senza effetuare alcun fetch.
 *
 * Comportamento:
 *  - Fetch status al mount / cambio threadId (solo WhatsApp).
 *  - Errore fetch → degradazione silenziosa (niente badge, niente errore UI).
 *  - Pausa attiva → badge warning + bottone "Restituisci a Bob".
 *  - Click → loading (bottone disabled) → DELETE bot-pause → badge sparisce
 *    + toast verde "Bob riattivato su questa chat"; errore → role="alert"
 *    vicino al bottone, badge resta.
 */
export function BotPausePanel({ threadId, source }: Props) {
  const [status, setStatus] = useState<BotStatus | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const toastTimerRef = useRef<number | null>(null);

  // Cleanup timer al dismount
  useEffect(
    () => () => {
      if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    },
    [],
  );

  // Fetch status quando cambia threadId (solo su WhatsApp)
  useEffect(() => {
    if (source !== "whatsapp") return;
    let cancelled = false;
    setStatus(null);
    setResumeError(null);
    setShowToast(false);
    void fetchBotStatus(threadId)
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        // Degradazione silenziosa: brain non raggiungibile o thread
        // non WhatsApp sul backend. Niente badge mostrato.
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId, source]);

  // Thread non WhatsApp → nessun render
  if (source !== "whatsapp") return null;

  // Pausa non attiva (o status non ancora caricato) → mostra solo toast se presente
  const pauseActive = status !== null && isPauseActive(status);
  if (!pauseActive) {
    return (
      <div>
        {showToast && (
          <div
            role="status"
            aria-live="polite"
            className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 max-w-sm rounded-md border border-green/30 bg-green-soft px-3.5 py-2 text-[12.5px] text-ink shadow-popover"
          >
            Bob riattivato su questa chat
          </div>
        )}
      </div>
    );
  }

  async function handleResume() {
    setIsResuming(true);
    setResumeError(null);
    try {
      await resumeBot(threadId);
      // Successo: rimuovi badge
      setStatus(null);
      // Mostra toast
      setShowToast(true);
      if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = window.setTimeout(() => {
        setShowToast(false);
        toastTimerRef.current = null;
      }, TOAST_MS);
    } catch (err) {
      setResumeError(prettifyApiError(err));
    } finally {
      setIsResuming(false);
    }
  }

  // Testo del badge
  const badgeText =
    status?.human_until && new Date(status.human_until) > new Date()
      ? `Bob in pausa fino alle ${formatUntil(status.human_until)}`
      : "Bob in pausa (presa in carico manuale)";

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {/* Badge warning: colore semantico amber, contrasto ≥4.5:1 */}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber-soft px-2.5 py-0.5 text-[12px] font-semibold text-ink">
        {/* Icona "pausa" SVG inline — stesso stroke/size delle icone del file */}
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
        {badgeText}
      </span>

      {/* Bottone "Restituisci a Bob" */}
      <button
        type="button"
        aria-label="Restituisci a Bob"
        onClick={() => void handleResume()}
        disabled={isResuming}
        className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-line-2 bg-card px-3 text-[12px] font-semibold text-ink transition-colors duration-150 hover:border-ink disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      >
        {isResuming ? (
          <>
            {/* Spinner inline (stesso pattern di RailButton busy) */}
            <svg
              className="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Riattivo…
          </>
        ) : (
          "Restituisci a Bob"
        )}
      </button>

      {/* Errore resume: role="alert" vicino al bottone */}
      {resumeError && (
        <span role="alert" className="text-[11.5px] text-red">
          {resumeError}
        </span>
      )}

      {/* Toast successo */}
      {showToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 max-w-sm rounded-md border border-green/30 bg-green-soft px-3.5 py-2 text-[12.5px] text-ink shadow-popover"
        >
          Bob riattivato su questa chat
        </div>
      )}
    </div>
  );
}
